import type { ImgHTMLAttributes } from "react";
import { blurhashUrl } from "@/core/blurhashUrl";
import { getPodcastImage, ImageSizes } from "@/core/helpers";

interface CoverImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "width" | "height"> {
    podcast: Pick<Podcast, "slug"> & { blurhash?: string };
    /** Which size to fetch; also the intrinsic width and height */
    size?: ImageSizes;
    /** Above the fold: fetched right away at high priority. Everything else waits until it is near. */
    priority?: boolean;
}

/**
 * A podcast cover with its blurhash behind it as the image's own background, so the placeholder shows at once
 * and needs no wrapper element. It sits at the same size as the image, so it never stretches the layout.
 * Give `priority` to covers in the first screenful, leave it off for the rest.
 */
export const CoverImage = ({ podcast, size = ImageSizes.Small, priority = false, style, ...rest }: CoverImageProps) => {
    const placeholder = blurhashUrl(podcast.blurhash);
    return (
        <img
            alt=""
            decoding="async"
            fetchPriority={priority ? "high" : "auto"}
            height={size}
            loading={priority ? "eager" : "lazy"}
            src={getPodcastImage(podcast as Podcast, size)}
            style={placeholder ? { backgroundImage: `url(${placeholder})`, backgroundSize: "cover", ...style } : style}
            width={size}
            {...rest}
        />
    );
};
